import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableExportService } from '../../services/table-export.service';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    NgIf,
    NgFor,
    MatTooltipModule,
    FormsModule,
    A11yModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TableComponent implements OnChanges {
  cdr: any;
  resetSearch() {
    throw new Error('Method not implemented.');
  }
  refresh() {
    throw new Error('Method not implemented.');
  }
  @Input() columns: string[] = [];
  @Input() actionButtonName: string = '';
  @Input() data: any[] = [];
  @Input() tableName: string = 'Table export';
  @Input() actionTemplate?: TemplateRef<any>;
  @Input() status?: TemplateRef<any>;
  @Input() loading: boolean = false;
  @Input() cursor: boolean = false;
  @Input() enableRowSelection: boolean = false;
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() statusClick = new EventEmitter<{ row: any; id: any }>();
  @Output() approvalStatusClick = new EventEmitter<{ row: any; id: any }>();
  @Output() noteUpdate = new EventEmitter<{ row: any; notes: string }>();
  @Output() toggleChange = new EventEmitter<{ row: any; checked: boolean }>();
  @Output() downloadClick = new EventEmitter<{ row: any; id: boolean }>();
  @Output() sortClick = new EventEmitter<{
    direction: '' | 'asc' | 'desc';
    column: string | null;
  }>();
  @Input() totalItems: number = 0;
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 10;
  @Input() sortColumn: string | null = null;
  @Input() sortDirection: 'asc' | 'desc' | '' = '';
  @Input() sortableColumns: string[] | null = null;
  @Input() clickableColumns: string[] = [];
  @Output() pageChange = new EventEmitter<number>();
  @Input() serverSidePagination: boolean = false;
  @Input() useEventStatusLabels: boolean = false;
  @Input() eventStatus: string | null = null;
  @Output() cellClick = new EventEmitter<{ row: any; column: string }>();
  @Output() statusLabelChange = new EventEmitter<{
    row: any;
    newStatus: string;
  }>();

  public isAnimating = false;
  selectedRows: Set<any> = new Set();
  selectAllChecked = false;
  currentEditingRow: any = null;
  notesError: any = null;
  editedNote = '';
  totalListItem = 0;
  openStatusDropdownRowId: any = null;
  isMobileView: boolean = window.innerWidth <= 560;

  get safeData(): any[] {
    return Array.isArray(this.data) ? this.data : [];
  }
  constructor(
    private exportService: TableExportService,
    private cd2r: ChangeDetectorRef,
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (
      this.currentEditingRow &&
      !(event.target as Element).closest('.notes-wrapper')
    ) {
      this.closeNoteEditor();
    }
    if (!(event.target as Element).closest('.status-label-dropdown-wrapper')) {
      this.openStatusDropdownRowId = null;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.isMobileView = window.innerWidth <= 560;
  }

  toggleStatusLabelDropdown(row: any, event: Event): void {
    event.stopPropagation();
    this.openStatusDropdownRowId =
      this.openStatusDropdownRowId === row.id ? null : row.id;
  }

  onSelectStatusLabel(row: any, newStatus: string, event: Event): void {
    event.stopPropagation();
    this.openStatusDropdownRowId = null;
    this.statusLabelChange.emit({ row, newStatus });
  }

  getEventStatusClass(status: string): string {
    if (
      status === 'TeacherReviewPending' ||
      status === 'Pending' ||
      status === 'NewNonOnboarded' ||
      status === 'Interested'
    )
      return 'status-interested';
    if (
      status === 'TeacherApproved' ||
      status === 'Teacher Approved' ||
      status === 'Approved'
    )
      return 'status-approved';
    if (status === 'TeacherRejected' || status === 'Unapproved')
      return 'status-unapproved';
    return 'status-pending';
  }

  getEventStatusLabel(status: string): string {
    if (
      status === 'TeacherReviewPending' ||
      status === 'Pending' ||
      status === 'NewNonOnboarded' ||
      status === 'Interested'
    )
      return 'Interested';
    if (status === 'TeacherApproved' || status === 'Teacher Approved')
      return 'Approved';
    if (status === 'TeacherRejected') return 'Unapproved';
    return status;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.serverSidePagination && this.totalItems > 0) {
        this.totalListItem = this.totalItems;
      } else {
        this.totalListItem = this.safeData.length;
      }

      const prev = changes['data'].previousValue;
      const curr = changes['data'].currentValue;

      if (!prev || prev.length !== curr?.length) {
        this.resetTableState();
        this.autoSelectInvalidRows();
      }
    }

    if (changes['data'] || changes['currentPage']) {
      this.updateSelectAllState();
    }
  }

  private resetTableState(): void {
    this.selectedRows.clear();
    this.selectAllChecked = false;
    this.currentEditingRow = null;
    this.editedNote = '';
    this.isAnimating = false;
  }

  private autoSelectInvalidRows(): void {
    if (!this.data) return;
    this.data?.forEach((row) => {
      if (!this.isRowValid(row)) {
        this.selectedRows.delete(row);
      }
    });

    this.updateSelectAllState();
    this.emitValidSelectedRows();
  }

  get paginatedData(): any[] {
    if (this.serverSidePagination) {
      return this.safeData;
    }
    let sorted = this.getSortedData();
    if (sorted.length === 0) {
      return [];
    }

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return sorted.slice(start, end);
  }

  getSortedData(): any[] {
    const data = this.safeData.slice();

    if (this.serverSidePagination) {
      return data;
    }

    if (!this.sortColumn || !this.sortDirection) {
      return data;
    }

    const isDateColumn = (columnName: any): boolean => {
      const dateColumnPatterns = [
        'date of birth',
        'dob',
        'date',
        'created',
        'registered',
        'updated',
        'last login',
      ];
      const lowerCol = columnName.toLowerCase();
      return dateColumnPatterns.some((pattern) => lowerCol.includes(pattern));
    };

    const parseDDMMYYYY = (dateStr: string): number | null => {
      if (!dateStr || typeof dateStr !== 'string') return null;

      const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!match) return null;

      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const year = parseInt(match[3], 10);
      const date = new Date(year, month, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        return date.getTime();
      }
      return null;
    };

    return data.sort((a, b) => {
      let aVal = a[this.sortColumn!];
      let bVal = b[this.sortColumn!];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (isDateColumn(this.sortColumn)) {
        // If value is an object (e.g. { date, time }), fall back to _dateTimeSort timestamp
        if (typeof aVal === 'object' || typeof bVal === 'object') {
          const aTs = typeof a['_dateTimeSort'] === 'number' ? a['_dateTimeSort'] : 0;
          const bTs = typeof b['_dateTimeSort'] === 'number' ? b['_dateTimeSort'] : 0;
          return this.sortDirection === 'asc' ? aTs - bTs : bTs - aTs;
        }

        const aTimestamp = parseDDMMYYYY(String(aVal));
        const bTimestamp = parseDDMMYYYY(String(bVal));

        if (aTimestamp != null && bTimestamp != null) {
          return this.sortDirection === 'asc'
            ? aTimestamp - bTimestamp
            : bTimestamp - aTimestamp;
        }
      }

      const aNum = typeof aVal === 'number' ? aVal : Number(aVal);
      const bNum = typeof bVal === 'number' ? bVal : Number(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return this.sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return this.sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return this.sortDirection === 'asc' ? 1 : -1;

      return 0;
    });
  }

  get totalPages(): number {
    const total = Math.ceil(this.totalListItem / this.pageSize);
    return Math.max(total, 1);
  }

  get hasPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  get hasNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  get startRecord(): number {
    if (this.totalListItem === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    const end = this.currentPage * this.pageSize;
    return Math.min(end, this.totalListItem);
  }

  onRowClick(row: any, event: MouseEvent) {
    if (row.isDownloading) {
      return;
    }

    const target = event.target as HTMLElement;

    if (
      target.tagName === 'INPUT' ||
      target.closest('.actions-cell') ||
      target.closest('[data-status-column]') ||
      target.closest('.edit-icon') ||
      target.closest('.note-editor-dropdown') ||
      target.closest('.downloading-state') ||
      target.closest('.toggle-switch') ||
      target.closest('.media-toggle-switch')
    ) {
      return;
    }

    this.rowClick.emit(row);
  }

  isColumnSortable(column: string): boolean {
    return !this.sortableColumns || this.sortableColumns.includes(column);
  }

  isClickableColumn(column: string): boolean {
    return this.clickableColumns.includes(column);
  }

  onCellValueClick(row: any, column: string, event: MouseEvent): void {
    event.stopPropagation();
    this.cellClick.emit({ row, column });
  }

  isToggleEnabled(row: any): boolean {
    if (this.eventStatus !== null) {
      const isApproved =
        row['event status'] === 'Approved' ||
        row.status === 'TeacherApproved' ||
        row.status === 'AdminApproved' ||
        row.status === 'Registered';
      const isEventAllowingAttendance =
        this.eventStatus === 'Ongoing' || this.eventStatus === 'Already Held';
      return isApproved && isEventAllowingAttendance;
    }

    const status =
      row.status ||
      row.registrationStatus ||
      row.attendanceStatus ||
      row.teacherStatus ||
      row.adminStatus;
    const isMainStatusValid =
      status === 'Registered' ||
      status === 'PreRegistered' ||
      status === 'UnregisteredAddedManually' ||
      status === 'NewNonOnboarded' ||
      status === 'TeacherApproved' ||
      status === 'AdminApproved';

    if (row.teacherStatus !== undefined && row.adminStatus !== undefined) {
      return (
        isMainStatusValid &&
        row.teacherStatus === true &&
        row.adminStatus === true
      );
    }

    return isMainStatusValid;
  }

  showDownloadButton(row: any): boolean {
    const normalizedValue = this.normalize(row['parent consent']);
    return normalizedValue === 'download';
  }

  getConsentStatusClass(value: any): string {
    const normalized = this.normalize(value);
    if (normalized === 'approved' || normalized === 'true') {
      return 'approved';
    }
    if (
      normalized === 'rejected' ||
      normalized === 'declined' ||
      normalized === 'false'
    ) {
      return 'rejected';
    }
    return 'pending';
  }

  isConsentApproved(value: any): boolean {
    const normalized = this.normalize(value);
    return normalized === 'approved' || normalized === 'true';
  }

  onToggleChange(row: any, event: Event): void {
    if (row.restrictToggle) return;
    if (!this.isToggleEnabled(row)) {
      event.preventDefault();
      (event.target as HTMLInputElement).checked = row.attended;
      return;
    }
    event.preventDefault();
    const currentState = row.attended;
    const intendedState = (event.target as HTMLInputElement).checked;
    (event.target as HTMLInputElement).checked = currentState;
    this.toggleChange.emit({ row, checked: intendedState });
  }

  onEditNotes(row: any, event: MouseEvent): void {
    event.stopPropagation();

    if (this.currentEditingRow === row) {
      this.closeNoteEditor();
      return;
    }
    this.currentEditingRow = row;
    this.editedNote = row.notes || '';
    setTimeout(() => {
      this.positionNoteEditor(event);
    }, 0);
  }

  onNoteChange(value: string) {
    this.notesError = null;
  }

  private positionNoteEditor(event: MouseEvent): void {
    const dropdown = document.querySelector(
      '.note-editor-dropdown',
    ) as HTMLElement;
    if (!dropdown) return;

    const target = event.target as HTMLElement;
    const editIcon = target.closest('.edit-icon') as HTMLElement;
    if (!editIcon) return;

    const iconRect = editIcon.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = dropdown.offsetHeight;
    const dropdownWidth = dropdown.offsetWidth;

    let top = iconRect.bottom + 8;
    let left = iconRect.left - dropdownWidth / 2 + iconRect.width / 2;

    if (top + dropdownHeight > viewportHeight) {
      top = iconRect.top - dropdownHeight - 8;
    }
    if (left < 10) {
      left = 10;
    } else if (left + dropdownWidth > viewportWidth - 10) {
      left = viewportWidth - dropdownWidth - 10;
    }

    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
  }

  closeNoteEditor(): void {
    this.notesError = null;
    this.currentEditingRow = null;
    this.editedNote = '';
  }

  saveNote(): void {
    if (this.currentEditingRow) {
      this.notesError = null;
      if (this.editedNote && this.editedNote.trim()) {
        this.currentEditingRow.notes = this.editedNote;
        this.noteUpdate.emit({
          row: this.currentEditingRow,
          notes: this.editedNote,
        });
        this.closeNoteEditor();
      } else {
        this.notesError = true;
      }
    }
  }

  onStatusClick(row: any, event: MouseEvent) {
    event.stopPropagation();
    if (row.id) {
      this.statusClick.emit({ row, id: row.id });
    }
  }

  onDownload(row: any, event: MouseEvent, type: boolean) {
    event.stopPropagation();
    if (row.isDownloading) {
      return;
    }
    row.isDownloading = true;
    this.downloadClick.emit({ row, id: type });

    setTimeout(() => {
      if (row.isDownloading) {
        row.isDownloading = false;
      }
    }, 30000);
  }

  onApprovalStatusClick(row: any, event: MouseEvent) {
    event.stopPropagation();
    if (row.id) {
      this.approvalStatusClick.emit({ row, id: row.id });
    }
  }

  getColspan(): number {
    let colspan = this.columns.length;
    if (this.enableRowSelection) colspan++;
    if (this.status) colspan++;
    if (this.actionTemplate) colspan++;
    return colspan;
  }

  toggleRowSelection(row: any) {
    if (!this.isRowValid(row)) {
      return;
    }

    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }

    this.updateSelectAllState();
    this.emitValidSelectedRows();
  }

  private emitValidSelectedRows() {
    const validSelected = Array.from(this.selectedRows).filter((row) =>
      this.isRowValid(row),
    );
    this.selectionChange.emit(validSelected);
  }

  toggleSelectAll() {
    if (!this.safeData.length) return;

    const validRowsOnCurrentPage = this.paginatedData.filter((row) =>
      this.isRowValid(row),
    );
    const allValidRowsSelected = validRowsOnCurrentPage.every((row) =>
      this.selectedRows.has(row),
    );

    if (allValidRowsSelected) {
      validRowsOnCurrentPage.forEach((row) => {
        this.selectedRows.delete(row);
      });
    } else {
      validRowsOnCurrentPage.forEach((row) => {
        this.selectedRows.add(row);
      });
    }

    this.updateSelectAllState();
    this.selectionChange.emit(Array.from(this.selectedRows));
  }

  private updateSelectAllState() {
    const validRowsOnCurrentPage = this.paginatedData.filter((row) =>
      this.isRowValid(row),
    );
    const selectedValidRowsOnCurrentPage = validRowsOnCurrentPage.filter(
      (row) => this.selectedRows.has(row),
    );
    this.selectAllChecked =
      validRowsOnCurrentPage.length > 0 &&
      selectedValidRowsOnCurrentPage.length === validRowsOnCurrentPage.length;
  }

  isRowSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  normalize(value: any): string {
    return (value || '').toString().trim().toLowerCase();
  }

  isRowValid(row: any): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d+$/;

    if (
      this.columns.includes('email') &&
      this.columns.includes('phoneNumber')
    ) {
      return (
        !!row.firstName &&
        !!row.lastName &&
        emailRegex.test(row.email || '') &&
        phoneRegex.test(row.phoneNumber || '')
      );
    }

    if (
      this.columns.includes('email (Personal)') &&
      this.columns.includes('mobileNumber')
    ) {
      return (
        !!row.firstName &&
        !!row.lastName &&
        emailRegex.test(row['email (Personal)'] || '') &&
        phoneRegex.test(row.mobileNumber || '')
      );
    }

    return this.columns.every(
      (col) => row[col] && row[col].toString().trim() !== '',
    );
  }

  goToPage(page: number) {
    if (page === this.currentPage || this.isAnimating) return;

    this.isAnimating = true;
    setTimeout(() => {
      if (this.serverSidePagination) {
        this.pageChange.emit(page);
      } else {
        this.currentPage = page;
        this.cdr?.detectChanges();
      }
      this.isAnimating = false;
    }, 300);
  }

  goToPreviousPage(): void {
    if (this.hasPreviousPage) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.hasNextPage) {
      this.goToPage(this.currentPage + 1);
    }
  }

  get paginationDisplay(): (number | string)[] {
    const pages: (number | string)[] = [];
    const total = this.totalPages;

    if (total <= 1) return pages;

    if (this.isMobileView) {
      const start = Math.max(1, this.currentPage - 1);
      const end = Math.min(total, start + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (total <= 7) {
      for (let i = 2; i <= total; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(2, this.currentPage - 2);
      let end = Math.min(total - 1, this.currentPage + 2);

      if (this.currentPage <= 3) {
        end = 5;
      }
      if (this.currentPage >= total - 2) {
        start = total - 4;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < total - 1) {
        pages.push('...');
      }

      pages.push(total);
    }

    return pages;
  }

  formatColumnHeader(column: string): string {
    if (column === 'PD points') {
      return 'PD points';
    }
    return (
      column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, '$1')
    );
  }

  cycleSort(column: string) {
    if (!this.isColumnSortable(column)) return;

    if (this.sortColumn !== column) {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else if (this.sortDirection === 'desc') {
      this.sortColumn = null;
      this.sortDirection = '';
    }

    this.currentPage = 1;

    if (!this.serverSidePagination) {
      this.pageChange.emit(1);
    }

    this.sortClick.emit({
      column: this.sortColumn,
      direction: this.sortDirection,
    });

    if (!this.serverSidePagination) {
      this.cd2r?.detectChanges();
    }
  }

  getSortImage(sortState: 'asc' | 'desc' | ''): string {
    if (sortState === 'asc') {
      return 'assets/images/down-arrow.svg';
    } else if (sortState === 'desc') {
      return 'assets/images/arrow-up.svg';
    } else {
      return 'assets/images/arrow-bi.svg';
    }
  }

  getSortState(column: string): 'asc' | 'desc' | '' {
    return this.sortColumn === column ? this.sortDirection : '';
  }

  mapDataByColumns(
    columns: readonly string[],
    data: Record<string, unknown>[],
  ): unknown[][] {
    return data.map((row) => columns.map((col) => row[col] ?? ''));
  }

  exportTable(type: 'pdf' | 'csv') {
    if (this.loading) {
      console.warn('table is loading');
      return;
    }

    let rawData = this.getSortedData();
    rawData = rawData.map((item: any) => {
      const transformed: any = {};
      Object.entries(item).forEach(([key, value]) => {
        if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          transformed[key] = Object.values(value)
            .filter((v) => v !== null && v !== undefined && v !== '')
            .join(', ');
        } else {
          transformed[key] = value;
        }
      });
      return transformed;
    });

    const tableData = this.mapDataByColumns(this.columns, rawData);

    this.exportService.exportTable(
      type,
      this.columns,
      tableData,
      `${this.tableName.replace(/\s+/g, '-').toLowerCase()}`,
    );
  }

  isClickable(row: any): boolean {
    return !(
      row.status === 'AdminApproved' ||
      row.status === 'TeacherApproved' ||
      row.status === 'Rejected' ||
      row.status === 'Not Registered' ||
      row.status === 'Submitted (Pending Teacher Approval)' ||
      row.status === 'Submitted (Pending Admin Approval)' ||
      row.status === 'TeacherRejected' ||
      row.status === 'TeacherReviewPending' ||
      row.status === 'Registered' ||
      row.status === 'Already Held' ||
      row.status === 'UnregisteredAddedManually' ||
      row.status === 'NewNonOnboarded'
    );
  }
}
